import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { ShieldCheck, ChartLineUp, Buildings, SignOut, Robot, Receipt, Users, Files, FileText, ChartBar } from "@phosphor-icons/react";
import { Toaster } from "@/components/ui/sonner";
import { getUser, clearSession } from "@/lib/sessionStore";
import AdminAlertsBell from "@/components/admin/AdminAlertsBell";
import { MobileTopBar, MobileDrawer } from "@/components/MobileShell";

const NAV = [
  { to: "/admin-master",                          end: true,  label: "Dashboard",      icon: ChartLineUp },
  { to: "/admin-master/tenants",                  end: false, label: "Clientes",       icon: Buildings },
  { to: "/admin-master/representantes",           end: false, label: "Representantes", icon: Users },
  { to: "/admin-master/materiais-representantes", end: false, label: "Materiais",      icon: Files },
  { to: "/admin-master/notas-fiscais",            end: false, label: "Notas Fiscais",  icon: FileText },
  { to: "/admin-master/ai-observability",         end: false, label: "IA",             icon: Robot },
  { to: "/admin-master/contas",                   end: false, label: "Contas",         icon: Receipt },
  // v0.99.9 · Wave Bonus · Fev/2026 — análise financeira de margens
  { to: "/admin-master/margens",                  end: false, label: "Margens",        icon: ChartBar },
  // v0.98 · Vertical GOV · Fev/2026
  { to: "/admin-master/gov",                       end: false, label: "Gestão Pública", icon: Buildings },
];

// v0.96.19 — extraído pro compartilhamento entre sidebar desktop e
// drawer mobile. Mesmo `<nav>`, sem duplicação de fonte da verdade.
function AdminNavContent({ user, onLogout, onNavigate }) {
  return (
    <div className="flex-1 flex flex-col py-6 px-4 h-full">
      <div className="flex items-center gap-2.5 mb-8 px-2">
        <div className="w-8 h-8 rounded-md bg-white/10 flex items-center justify-center">
          <ShieldCheck size={16} weight="regular" />
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-[0.22em] text-slate-500 font-medium leading-none">AgentUS</div>
          <div className="font-heading text-[13px] font-semibold tracking-tight leading-tight mt-1">Admin Master</div>
        </div>
      </div>

      <nav className="flex-1 space-y-0.5">
        {NAV.map(({ to, end, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={onNavigate}
            data-testid={`admin-nav-${label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2 rounded-md text-[13px] transition-colors ${
                isActive
                  ? "bg-white/10 text-white"
                  : "text-slate-400 hover:text-white hover:bg-white/5"
              }`
            }
          >
            <Icon size={14} weight="regular" /> {label}
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-white/5 pt-4 mt-4">
        <div className="px-2 mb-3">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-medium">Conectado como</div>
          <div className="text-[13px] text-white truncate mt-1">{user.email}</div>
        </div>
        <button
          onClick={onLogout}
          data-testid="admin-logout"
          className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-[13px] text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
        >
          <SignOut size={14} weight="regular" /> Sair
        </button>
      </div>
    </div>
  );
}

export default function AdminShell() {
  const navigate = useNavigate();
  const user = getUser();
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (!user || user.role !== "super_admin") {
      navigate("/admin-master/login", { replace: true });
    }
  }, [user, navigate]);

  if (!user || user.role !== "super_admin") return null;

  const logout = () => {
    clearSession();
    navigate("/admin-master/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-slate-100 font-body md:flex" data-testid="admin-shell">
      {/* TopBar mobile — só aparece < 768px. Desktop nem monta. */}
      <MobileTopBar
        title="Admin Master"
        onMenu={() => setDrawerOpen(true)}
        right={<AdminAlertsBell />}
      />

      {/* Drawer mobile — mesmo conteúdo do sidebar desktop, sem duplicar */}
      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <AdminNavContent
          user={user}
          onLogout={logout}
          onNavigate={() => setDrawerOpen(false)}
        />
      </MobileDrawer>

      {/* Sidebar desktop — comportamento intocado, só `hidden md:flex` novo */}
      <aside className="hidden md:flex w-60 shrink-0 border-r border-white/5 flex-col sticky top-0 h-screen">
        <AdminNavContent user={user} onLogout={logout} />
      </aside>

      <main className="flex-1 min-w-0 overflow-x-hidden relative">
        {/* Sino de alertas — visível apenas no desktop (mobile já tem no TopBar) */}
        <div className="hidden md:flex sticky top-0 z-30 justify-end items-center gap-2 px-6 py-2 bg-[#0A0A0B]/80 backdrop-blur border-b border-white/5">
          <AdminAlertsBell />
        </div>
        <Outlet />
      </main>
      <Toaster theme="dark" position="top-right" richColors />
    </div>
  );
}
